import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Calendar,
  Statistic,
  Badge,
  Divider
} from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";
import { getDataForPeriod } from "./chartUtils";
import "./CalendarView.scss";

const CalendarView = ({
  viewType,
  chartData,       
  rawChartData,     
  effectiveStartRange,
  effectiveEndRange
}) => {
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(moment());
  const [selectedPeriodStats, setSelectedPeriodStats] = useState(null);

  const getDataStatsForDate = useCallback((date, period = 'day') => {
    if (period === 'day') {
      if (viewType === "Daily") {
        // For daily view, use the processed chartData
        if (!chartData?.length) return 0;
        const dateKey = moment(date).format("YYYY/MM/DD");
        const dayData = chartData.find(item => {
          // Ensure we're comparing strings, handle both string and moment object cases
          const itemDate = typeof item.date2 === 'string' ? item.date2 : moment(item.date2).format("YYYY/MM/DD");
          return itemDate === dateKey;
        });
        const value = dayData ? dayData.value : 0;
        
        return value;
      } else {
        if (!rawChartData?.length) return 0;
        const dateKey = moment(date).format("YYYY/MM/DD");
        const dayData = rawChartData.find(item => {
          // Ensure we're comparing strings, handle both string and moment object cases
          const itemDate = typeof item.date2 === 'string' ? item.date2 : moment(item.date2).format("YYYY/MM/DD");
          return itemDate === dateKey;
        });
        const value = dayData ? dayData.value : 0;
    
        return value;
      }
    } else if (period === 'month') {
      if (viewType !== "Monthly" || !chartData?.length) return 0;
      const monthKey = moment(date).format("MMM YYYY");
      const monthData = chartData.find(item => {
        // Ensure we're comparing strings, handle both string and moment object cases
        const itemDate = typeof item.date2 === 'string' ? item.date2 : moment(item.date2).format("MMM YYYY");
        return itemDate === monthKey;
      });
      const value = monthData ? monthData.value : 0;
      
      return value;
    } else if (period === 'week') {
      if (viewType !== "Weekly" || !chartData?.length) return 0;
      const weekStart = moment(date).startOf('week');
      const weekEnd = weekStart.clone().endOf('week');
      let weekLabel;
      if (weekStart.year() === weekEnd.year()) {
        weekLabel = `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')} ${weekStart.year()}`;
      } else {
        weekLabel = `${weekStart.format('MMM DD, YYYY')} - ${weekEnd.format('MMM DD, YYYY')}`;
      }
      const weekData = chartData.find(item => {
        // Ensure we're comparing strings, handle both string and moment object cases
        const itemDate = typeof item.date2 === 'string' ? item.date2 : moment(item.date2).format("MMM DD - MMM DD YYYY");
        return itemDate === weekLabel;
      });
      const value = weekData ? weekData.value : 0;
    
      
      return value;
    }
    return 0;
  }, [rawChartData, chartData, viewType]);

  const getDailyBreakdown = useCallback((startDate, endDate) => {
    if (!rawChartData?.length) return [];
    
    const periodData = getDataForPeriod(rawChartData, startDate, endDate);
    const breakdown = [];
    
    periodData.forEach(item => {
      if (item.value > 0) {
        breakdown.push({
          date: moment(item.date2, "YYYY/MM/DD"),
          count: item.value,
          displayDate: moment(item.date2, "YYYY/MM/DD").format("MMM Do")
        });
      }
    });
    
    return breakdown.sort((a, b) => a.date.diff(b.date));
  }, [rawChartData]);

  const getPeriodLabel = useCallback((date, viewType) => {
    const selected = moment(date);
    switch (viewType) {
      case "Daily":
        return selected.format("MMMM Do, YYYY");
      case "Weekly":
        const weekStart = selected.clone().startOf('week');
        const weekEnd = selected.clone().endOf('week');
        // Handle cross-year weeks
        if (weekStart.year() === weekEnd.year()) {
          return `Week of ${weekStart.format("MMM Do")} - ${weekEnd.format("MMM Do, YYYY")}`;
        } else {
          return `Week of ${weekStart.format("MMM Do, YYYY")} - ${weekEnd.format("MMM Do, YYYY")}`;
        }
      case "Monthly":
        return selected.format("MMMM YYYY");
      default:
        return "";
    }
  }, []);

  const getDateDataForPeriod = useCallback((selectedDate, viewType) => {
    const selected = moment(selectedDate);
    let startDate, endDate, totalValue;
    
    switch (viewType) {
      case "Daily":
        startDate = selected.format("YYYY-MM-DD");
        endDate = startDate;
        totalValue = getDataStatsForDate(selected, 'day');
        return {
          totalValue,
          startDate,
          endDate,
          periodLabel: getPeriodLabel(selected, viewType),
          dailyBreakdown: []
        };
      case "Weekly":
        const weekStart = selected.clone().startOf('week');
        const weekEnd = weekStart.clone().endOf('week');
        startDate = weekStart.format("YYYY-MM-DD");
        endDate = weekEnd.format("YYYY-MM-DD");
        
        totalValue = getDataStatsForDate(selected, 'week');
        break;
      case "Monthly":
        const monthStart = selected.clone().startOf('month');
        const monthEnd = monthStart.clone().endOf('month');
        startDate = monthStart.format("YYYY-MM-DD");
        endDate = monthEnd.format("YYYY-MM-DD");
        
        totalValue = getDataStatsForDate(selected, 'month');
        break;
      default:
        return null;
    }
    
    return {
      totalValue,
      startDate,
      endDate,
      periodLabel: getPeriodLabel(selected, viewType),
      dailyBreakdown: getDailyBreakdown(startDate, endDate)
    };
  }, [chartData, viewType, getDataStatsForDate, getPeriodLabel, getDailyBreakdown]);

  
  
  const disabledDate = useCallback((current) => {
    if (!effectiveStartRange || !effectiveEndRange) return false;
    
    const startDate = moment(effectiveStartRange);
    const endDate = moment(effectiveEndRange);
    
    if (viewType === "Monthly") {
      return current.isBefore(startDate, 'month') || current.isAfter(endDate, 'month');
    } else {
      return current.isBefore(startDate, 'day') || current.isAfter(endDate, 'day');
    }
  }, [effectiveStartRange, effectiveEndRange, viewType]);
  

  useEffect(() => {
    if (effectiveStartRange && effectiveEndRange) {
      const startDate = moment(effectiveStartRange);
      const endDate = moment(effectiveEndRange);
      
      if (selectedCalendarDate.isBefore(startDate, 'day') || selectedCalendarDate.isAfter(endDate, 'day')) {
        const today = moment();
        const newDate = today.isBetween(startDate, endDate, 'day', '[]') ? today : startDate.clone();
        setSelectedCalendarDate(newDate);
      }
    }
  }, [effectiveStartRange, effectiveEndRange, viewType, selectedCalendarDate]);

  useEffect(() => {
    const stats = getDateDataForPeriod(selectedCalendarDate, viewType);
    setSelectedPeriodStats(stats);
    

  }, [selectedCalendarDate, viewType, getDateDataForPeriod, chartData, rawChartData]);

  const getIntensityColor = useMemo(() => (value) => {
    if (value >= 10) return '#1890ff';
    if (value >= 5) return '#52c41a';
    if (value >= 1) return '#faad14';
    return '#d9d9d9';
  }, []);

  const dateCellRender = useCallback((value) => {
    let totalForDay = 0;
    
    if (viewType === "Daily") {
      totalForDay = getDataStatsForDate(value, 'day');
    } else if (viewType === "Weekly") {
      totalForDay = getDataStatsForDate(value, 'week');
    } else if (viewType === "Monthly") {
      totalForDay = getDataStatsForDate(value, 'day');
    }
    
    const isInChartRange = value.isBetween(effectiveStartRange, effectiveEndRange, 'day', '[]');
    const isToday = value.isSame(moment(), 'day');
    const isSelected = value.isSame(selectedCalendarDate, 'day');
    const isDisabled = disabledDate(value);
    
    return (
      <div className="calendar-month-overlay">
        {!isDisabled && <div className="enabled-date-indicator" />}
        {isInChartRange && <div className="chart-range-indicator" />}
        {isSelected && <div className="selected-date-indicator" />}
        {isToday && <div className="today-indicator" />}
        {totalForDay > 0 && (
          <div className="data-badge">
            <Badge 
              count={totalForDay}
              overflowCount={999999} 
              style={{ backgroundColor: '#52c41a'}}
            />
          </div>
        )}
      </div>
    );
  }, [getDataStatsForDate, effectiveStartRange, effectiveEndRange, selectedCalendarDate, disabledDate, getIntensityColor, viewType]);
  
  const monthCellRender = useCallback((value) => {
    if (viewType !== "Monthly") return null;
    
    const totalForMonth = getDataStatsForDate(value, 'month');
    const isDisabled = disabledDate(value);
    const isSelected = value.isSame(selectedCalendarDate, 'month');
    
    return (
      <div className="calendar-month-overlay">
        {!isDisabled && <div className="enabled-month-indicator" />}
        {totalForMonth > 0 && (
          <div className="month-data-value">
            {totalForMonth}
          </div>
        )}
        {isSelected && <div className="selected-month-indicator" />}
        
      </div>
    );
  }, [viewType, getDataStatsForDate, selectedCalendarDate, disabledDate]);

  const onCalendarSelect = useCallback((date) => {
    setSelectedCalendarDate(date);
  }, []);

  const renderWeeklyNavigation = useMemo(() => {
    if (viewType !== "Weekly" || !chartData?.length) {
      return null;
    }
    
    const startDate = moment(effectiveStartRange);
    const endDate = moment(effectiveEndRange);
    const weeks = [];
    
    let currentWeekStart = startDate.clone().startOf('week');
    while (currentWeekStart.isSameOrBefore(endDate, 'day')) {
      const weekEnd = currentWeekStart.clone().endOf('week');
      
      let weekLabel;
      if (currentWeekStart.year() === weekEnd.year()) {
        weekLabel = `${currentWeekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')} ${currentWeekStart.year()}`;
      } else {
        weekLabel = `${currentWeekStart.format('MMM DD, YYYY')} - ${weekEnd.format('MMM DD, YYYY')}`;
      }
      
      const weekData = chartData.find(item => {
        // Ensure we're comparing strings, handle both string and moment object cases
        const itemDate = typeof item.date2 === 'string' ? item.date2 : moment(item.date2).format("MMM DD - MMM DD YYYY");
        return itemDate === weekLabel;
      });
      const weekTotal = weekData ? weekData.value : 0;
      
      let weekDisplay;
      if (currentWeekStart.year() === weekEnd.year()) {
        weekDisplay = `${currentWeekStart.format('MMM Do')} - ${weekEnd.format('MMM Do, YYYY')}`;
      } else {
        weekDisplay = `${currentWeekStart.format('MMM Do, YYYY')} - ${weekEnd.format('MMM Do, YYYY')}`;
      }
      
      weeks.push({
        start: currentWeekStart.clone(),
        end: weekEnd.clone(),
        total: weekTotal,
        display: weekDisplay,
        isSelected: currentWeekStart.isSame(selectedCalendarDate.clone().startOf('week'), 'week')
      });
      
      currentWeekStart.add(1, 'week');
    }
    
    return (
      <div className="weekly-navigation">
        {weeks.map((week) => (
          <Card
            key={`week-${week.start.format('YYYY-MM-DD')}`}
            size="small"
            hoverable
            className={`week-card ${week.isSelected ? 'selected' : ''}`}
            onClick={() => setSelectedCalendarDate(week.start.clone().add(3, 'days'))}
          >
            <div className="week-content">
              <Typography.Text strong className="week-title">
                {week.display}
              </Typography.Text>
              <Badge 
                count={week.total}
                overflowCount={999999} 
                style={{ 
                  backgroundColor: week.total > 0 ? '#52c41a' : '#d9d9d9'
                }}
              />
            </div>
          </Card>
        ))}
      </div>
    );
  }, [effectiveStartRange, effectiveEndRange, selectedCalendarDate, chartData, viewType]);

  return (
    <div className="interactive-calendar-view">
      <div className="date-range-header">
        <Typography.Title level={4} className="range-title">
          📅 {viewType} Calendar View
        </Typography.Title>
        <Typography.Text className="range-dates">
          Viewing data from {moment(effectiveStartRange).format("MMM Do, YYYY")} 
          {" to "}{moment(effectiveEndRange).format("MMM Do, YYYY")}
        </Typography.Text>
      </div>

      {selectedPeriodStats && (
        <div className="selected-period-header">
          <Typography.Title level={5} className="selected-period-title">
            🗓️ Currently Selected: {selectedPeriodStats.periodLabel}
          </Typography.Title>
          {selectedPeriodStats.totalValue > 0 && (
            <Typography.Text className="selected-period-summary">
              {selectedPeriodStats.totalValue} product models delivered
              {selectedPeriodStats.startDate !== selectedPeriodStats.endDate && 
                ` from ${moment(selectedPeriodStats.startDate).format("MMM Do")} to ${moment(selectedPeriodStats.endDate).format("MMM Do")}`
              }
            </Typography.Text>
          )}
        </div>
      )}

      <Row gutter={[24, 24]} className="calendar-main-row">
        <Col span={viewType === "Weekly" ? 15 : 16}>
          {viewType === "Weekly" ? (
            <Card 
              title={
                <div className="card-header-flex">
                  <CalendarOutlined />
                  <span>Weekly Navigation</span>
                </div>
              }
              size="small"
              className="card-standard"
            >
              <div className="weekly-selected-header">
                <Typography.Text strong className="selected-week-text">
                  Selected Week: {(() => {
                    const weekStart = selectedCalendarDate.clone().startOf('week');
                    const weekEnd = selectedCalendarDate.clone().endOf('week');
                    if (weekStart.year() === weekEnd.year()) {
                      return `${weekStart.format("MMM Do")} - ${weekEnd.format("MMM Do, YYYY")}`;
                    } else {
                      return `${weekStart.format("MMM Do, YYYY")} - ${weekEnd.format("MMM Do, YYYY")}`;
                    }
                  })()}
                </Typography.Text>
              </div>
              {renderWeeklyNavigation}
            </Card>
          ) : (
            <Card 
              title={
                <div className="card-header-flex">
                  <CalendarOutlined />
                  <span>{viewType} Calendar View</span>
                </div>
              }
              size="small"
              extra={
                <Typography.Text type="secondary" className="card-extra-text">
                  Click on any date to view statistics
                </Typography.Text>
              }
              className="card-standard"
            >
              <div className="calendar-container">
                <Calendar
                  value={selectedCalendarDate}
                  onSelect={onCalendarSelect}
                  dateCellRender={dateCellRender}
                  monthCellRender={viewType === "Monthly" ? monthCellRender : null}
                  mode={viewType === "Monthly" ? "year" : "month"}
                  disabledDate={viewType !== "Monthly" ? disabledDate : undefined}
                  validRange={viewType === "Monthly" ? [moment(effectiveStartRange), moment(effectiveEndRange)] : undefined}
                  className="enhanced-calendar"
                />
              </div>
            </Card>
          )}
        </Col>
        
        <Col span={viewType === "Weekly" ? 9 : 8}>
          {selectedPeriodStats && selectedPeriodStats.totalValue > 0 ? (
            <Card 
              title={
                <div className="card-header-flex">
                  <span>📊</span>
                  <span>{viewType === "Weekly" ? "Weekly Statistics" : "Period Statistics"}</span>
                </div>
              }
              size="small"
              className="statistics-panel"
            >
              <div className="period-header">
                <Typography.Title level={5} className="period-title">
                  {selectedPeriodStats.periodLabel}
                </Typography.Title>
              </div>
              
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Card size="small" className="total-stat-card">
                    <Statistic
                      title="Product Models Delivered"
                      value={selectedPeriodStats.totalValue}
                      precision={0}
                      valueStyle={{ 
                        color: '#1890ff', 
                        fontSize: viewType === "Weekly" ? '20px' : '22px' 
                      }}
                      prefix="🎯"
                    />
                  </Card>
                </Col>
                
                {(viewType === "Weekly" || viewType === "Monthly") && selectedPeriodStats.dailyBreakdown?.length > 0 && (
                  <Col span={24}>
                    <Card size="small" className="daily-breakdown-card">
                      <Typography.Text strong className="breakdown-title">
                        📊 Daily Breakdown
                      </Typography.Text>
                      <div 
                        className={`daily-breakdown-scroll ${viewType === "Monthly" ? 'monthly-view' : 'weekly-view'}`}
                      >
                        {selectedPeriodStats.dailyBreakdown.map((day, index) => (
                          <div key={index} className="breakdown-item">
                            <Typography.Text className="item-date">
                              {day.displayDate}
                            </Typography.Text>
                            <Badge 
                              count={day.count}
                              overflowCount={999999}
                            />
                          </div>
                        ))}
                      </div>
                      
                    </Card>
                  </Col>
                )}
                
                <Col span={24}>
                  <Divider className="period-divider" />
                  <div className="period-info">
                    <Typography.Text type="secondary" className="period-date-info">
                      📅 {moment(selectedPeriodStats.startDate).format("MMM Do")} 
                      {selectedPeriodStats.startDate !== selectedPeriodStats.endDate && 
                        ` - ${moment(selectedPeriodStats.endDate).format("MMM Do")}`
                      }
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
            </Card>
          ) : (
            <Card 
              size="small"
              className="no-data-card"
            >
              <div className="no-data-content">
                <div className="no-data-icon">📭</div>
                <Typography.Title level={5} type="secondary" className="no-data-title">
                  No Data Available
                </Typography.Title>
                <Typography.Text type="secondary">
                  No models were delivered during the selected {viewType.toLowerCase()} period.
                </Typography.Text>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CalendarView;
